import React, {useState, useEffect, useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Hooks
import useApi from "../../hooks/useApi.js";

// Utils
import {FACILITY_NAME_MAX_LENGTH, facilityNameFitsSlugLimit} from "../../utils/helpers.js";

// Components
import Help from "../../components/Help.js";
import Modal from "../../components/Modal.js";
import InputTextGroupDraggable from "../../components/form/InputTextGroupDraggable.js";
import {InputText, InputCheck, InputSelect} from "../../components/form/FormInputs.js";

// Style
import {pad} from "../../style/components/variables.js";
import {
  FormField,
  Button,
  Form,
  FormGroup,
  ButtonLoader,
  HeadingCenter,
  Label,
  Text,
  Pill
} from "../../style/components/general.js";
import { breakpoint } from "../../style/components/breakpoints.js";

const NAME_MAX_MESSAGE = "Facility name must be 80 characters or less.";
const NAME_SLUG_MESSAGE = "Facility name is too long. Please shorten the name and try again.";

const getTypeNames = facility => {
  const fromTypes = (facility?.types || []).map(({name}) => name).filter(Boolean);
  if (fromTypes.length) return fromTypes;
  return facility?.type ? [facility.type] : [];
};

const schema = yup.object().shape({
  name: yup
    .string()
    .required("Please provide name")
    .max(FACILITY_NAME_MAX_LENGTH, NAME_MAX_MESSAGE)
    .test("slug-length", NAME_SLUG_MESSAGE, (value, context) =>
      facilityNameFitsSlugLimit(value, context.options.context?.typeNames)
    ),
  details: yup.object().shape({
    region: yup.string(),
    hasJobs: yup.bool(),
    jobs: yup.array().when("hasJobs", {
      is: val => !!val,
      then: () =>
        yup.array().of(
          yup.object().shape({
            option: yup.string().required("Job # required when checked")
          })
        )
    }),
    hasNotes: yup.bool(),
    notes: yup.array().when("hasNotes", {
      is: val => !!val,
      then: () =>
        yup.array().of(
          yup.object().shape({
            key: yup.string().required("Key required when checked"),
            value: yup.string().required("Value required when checked")
          })
        )
    }),
    hasContacts: yup.bool(),
    contacts: yup.array().when("hasContacts", {
      is: val => !!val,
      then: () =>
        yup.array().test({
          message: "At least one email is required.",
          test: arr => arr.length > 0
        })
    })
  })
});

const getRegionHelp = (loading, error, regions) => {
  if (loading) return undefined;
  if (error) return "Regions could not be loaded. Try loading them again.";
  if (regions?.length) return "Regions group facilities for filtering and reporting from the Facilities Dashboard. If a region is missing please contact your system admin.";
  return "No regions are available. Please reach out to your system admin if regions are required.";
};

const ModalEditFacility = ({visible, setVisible, facility, setFacility}) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState();
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionsError, setRegionsError] = useState(false);

  const {api: apiFacility} = useApi("facilities");
  const {api: apiRegion} = useApi("regions");

  const form = useForm({
    defaultValues: {
      ...facility,
      region: facility.region?.id,
      details: {
        ...facility.details,
        hasJobs: facility.details?.jobs?.length > 0,
        jobs:
          facility.details?.jobs?.length > 0
            ? facility.details.jobs?.map(job => ({option: job}))
            : [{option: ""}],
        hasNotes: facility.details?.notes?.length > 0,
        notes: facility.details?.notes?.length > 0 ? facility.details.notes : [{key: "", value: ""}]
      }
    },
    resolver: yupResolver(schema),
    context: {typeNames: getTypeNames(facility)}
  });
  const {setValue, setError, handleSubmit, watch} = form;

  const hasJobs = watch("details.hasJobs");
  const hasNotes = watch("details.hasNotes");

  const regionHelp = getRegionHelp(regionsLoading, regionsError, regions);

  useEffect(() => {
    const {details} = facility;
    if (!hasJobs && !details.jobs) setValue("details.jobs", [{option: ""}]);
    if (!hasNotes && !details.notes) setValue("details.notes", [{key: "", value: ""}]);
  }, [facility, hasJobs, hasNotes, setValue]);

  const loadRegions = useCallback(() => {
    setRegionsLoading(true);
    setRegionsError(false);

    apiRegion
      .callGet()
      .then(response => {
        if (response?.status === 200 && response.data) {
          setRegions(response.data);
        } else {
          setRegionsError(true);
        }
      })
      .catch(() => setRegionsError(true))
      .finally(() => setRegionsLoading(false));
  }, [apiRegion]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const editFacility = values => {
    setLoading(true);

    const {name, region, details} = values;

    const sanitizedName = name.replace(/\s+/g, " ").trim();

    const editFacilityData = {
      name: sanitizedName,
      regionId: region || null,
      details: {
        jobs:
          details.hasJobs && details?.jobs?.length > 0 ? details.jobs.map(job => job.option) : [],
        notes: details.hasNotes && details?.notes?.length > 0 ? details.notes : []
      }
    };

    apiFacility
      .callPut(facility.id, editFacilityData)
      .then(response => {
        if (response?.status === 200 && response.data) {
          const updated = response.data.facility;
          setFacility(prev => ({
            ...prev,
            name: updated.name,
            slug: updated.slug,
            region: updated.region,
            details: updated.details,
            addresses: updated.addresses
          }));

          // Preserve the currently selected dashboard tab after a facility rename.
          const matchedPathName = pathname.match(/^\/.+\/.+\/(.+)\/?/);
          const tabPathName = matchedPathName ? `/${matchedPathName[1]}` : "";
          navigate(`/facilities/${updated.slug}${tabPathName}`);
          setVisible(false);
        } else if (typeof response === "string") {
          setError("name", {type: "server", message: response});
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    facility && (
      <Modal visible={visible} setVisible={setVisible} maxWidth={breakpoint.width[2]}>
        <ModalTitle>Edit Facility Information</ModalTitle>

        <FormProvider {...form}>
          <Form noValidate onSubmit={handleSubmit(editFacility)}>
            <FormGroup>
              <FormField>
                <InputText
                  name="name"
                  placeholder="Name"
                  label="Name"
                  required
                  maxLength={FACILITY_NAME_MAX_LENGTH}
                  disabled={!facility?.type}
                />
                {!facility?.type && (
                  <WarningText quiet>Enterprise Facility name cannot be edited</WarningText>
                )}
              </FormField>

              {facility.type && (
                <FormField>
                  <Label bold>TYPE&nbsp;<Help>Each facility type is an independent version of this facility. Selecting multiple types creates a separate facility for each type, with its own builder configuration, checksheets, and related records. The facility name and region are shared across the selected types.</Help></Label>
                  <Pill>{facility?.type}</Pill>
                </FormField>
              )}

              <RegionField>
                <InputSelect
                  name="region"
                  label="Region"
                  help={regionHelp}
                  placeholder={regionsLoading ? "Loading regions..." : "Assign Region"}
                  disabled={regionsLoading || !regions?.length}
                  options={regions?.map(({id, name}) => ({label: (name ?? "").toUpperCase(), value: id}))}
                />
                {regionsLoading && (
                  <RegionLoadingOverlay aria-live="polite" aria-label="Loading regions">
                    <ButtonLoader />
                  </RegionLoadingOverlay>
                )}
                {regionsError && (
                  <RetryRegions type="button" onClick={loadRegions}>
                    Retry loading regions
                  </RetryRegions>
                )}
              </RegionField>

              <InputCheck name="details.hasJobs">Include Job number(s)</InputCheck>
              {hasJobs && (
                <FormField>
                  <InputTextGroupDraggable
                    name="details.jobs"
                    valuePlaceholder="Job #"
                    limit={15}
                  />
                </FormField>
              )}

              <InputCheck name="details.hasNotes">Include Note(s)</InputCheck>
              {hasNotes && (
                <FormField>
                  <InputTextGroupDraggable
                    name="details.notes"
                    keyPlaceholder="Label"
                    canSetKey
                  />
                </FormField>
              )}
            </FormGroup>

            <FormField>
              <Submit type="submit" loading={loading ? 1 : 0}>
                Save {loading && <ButtonLoader />}
              </Submit>
            </FormField>
          </Form>
        </FormProvider>
      </Modal>
    )
  );
};

ModalEditFacility.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  setFacility: PropTypes.func.isRequired
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin: ${pad}px 0;
`;

const Submit = styled(Button)`
  margin-bottom: ${pad}px;
`;

const WarningText = styled(Text)`
  margin-top: 5px;
`;

const RegionField = styled(FormField)`
  position: relative;
`;

const RegionLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: ${({theme}) => `${theme.tertiary}cc`};
`;

const RetryRegions = styled(Button)`
  margin-top: ${pad / 2}px;
`;

export default ModalEditFacility;
